
















import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { Editor as TipTapEditor, EditorContent } from '@tiptap/vue-2';
import debounce from 'debounce';
import Bold from '@tiptap/extension-bold';
import Italic from '@tiptap/extension-italic';
import Link from '@tiptap/extension-link';
import Strike from '@tiptap/extension-strike';
import Underline from '@tiptap/extension-underline';
import Superscript from '@tiptap/extension-superscript';
import Text from '@tiptap/extension-text';
import { columnResizingPluginKey } from 'prosemirror-tables';
import type { EditorView } from 'prosemirror-view';
import type { ITextContentDoc, IMergeValues, IField } from '@getaccept/editor-lib';
import {
  EColors,
  EProsemirrorType,
  ListItemNode,
  BulletListNode,
  HeadingNode,
  OrderedListNode,
  ParagraphNode,
  TableCellNode,
  TableNode,
  TableHeaderNode,
  TableRowNode,
  TableDocNode,
  MergeValueNode,
  HardBreakNode,
  TextAlign,
  EditorHelper,
} from '@getaccept/editor-lib';
import { updateMergeValues, setContent, handleInit } from './helpers/prosemirror-viewer.helper';

@Component({
  components: {
    EditorContent,
  },
  data: () => ({
    EColors,
  }),
})
export default class ProsemirrorViewerTable extends Vue {
  @Prop() borderColor: string;
  @Prop() alternateRowColor: string;
  @Prop() headerRowBackgroundColor: string;
  @Prop() isPdfViewer: boolean;
  @Prop() content: ITextContentDoc;
  @Prop() mergeValues: IMergeValues;
  @Prop() fields: IField[];

  @Watch('fields')
  @Watch('mergeValues')
  watchMergeValueUpdates() {
    updateMergeValues(this.contentPayload);
  }
  @Watch('content') watchContent() {
    setContent(this.contentPayload);
  }

  debounceHandleScroll: () => void = debounce(this.setOverflow, 300);
  overflow = false;
  tableWrapper: HTMLElement = undefined;
  viewer: TipTapEditor | null = null;

  get contentPayload() {
    return {
      viewer: this.viewer,
      content: this.content,
      fields: this.fields,
      mergeValues: this.mergeValues,
    };
  }

  get tableViewer() {
    return {
      content: this.content,
      editable: false,
      enableInputRules: false,
      enablePasteRules: false,
      extensions: [
        TableDocNode,
        ParagraphNode,
        Text,
        Bold,
        Italic,
        Strike,
        Superscript,
        Underline,
        OrderedListNode,
        BulletListNode,
        ListItemNode,
        MergeValueNode,
        Link.configure({
          autolink: false,
          openOnClick: true,
          linkOnPaste: false,
        }),
        TableNode.configure({ resizable: !this.isPdfViewer }),
        TableHeaderNode,
        TableCellNode,
        TableRowNode,
        HeadingNode,
        HardBreakNode,
        TextAlign.configure({
          types: [
            EProsemirrorType.Paragraph,
            EProsemirrorType.Heading,
            EProsemirrorType.BulletList,
            EProsemirrorType.OrderedList,
          ],
        }),
      ],
      onCreate: this.handleLoaded,
      editorProps: {
        handleDOMEvents: {
          mousedown: this.handleMouseDown,
        },
      },
    };
  }

  handleMouseDown({ state }: EditorView): boolean {
    return columnResizingPluginKey.getState(state).activeHandle !== -1;
  }

  setOverflow() {
    this.overflow = EditorHelper.hasHiddenContentRight(this.tableWrapper);
  }

  handleLoaded() {
    handleInit(this.viewer, this.fields, this.mergeValues);

    this.tableWrapper = (this.$refs.editorContainer as HTMLElement)?.querySelector('.tableWrapper');
    if (!this.tableWrapper) {
      return;
    }

    this.tableWrapper?.addEventListener('scroll', this.debounceHandleScroll);
    this.setOverflow();
  }

  mounted() {
    this.viewer = new TipTapEditor(this.tableViewer);
  }

  beforeDestroy() {
    this.tableWrapper?.removeEventListener('scroll', this.debounceHandleScroll);
    this.viewer.destroy();
  }
}
